import { Check, Pencil } from "lucide-react";

import { CodeBlock } from "@/components/cinch/CodeBlock";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MarkdownView } from "@/lib/markdown";
import { cn } from "@/lib/utils";

import type { GlossaryTerm, TermKind } from "../api";

const KIND_VARIANT: Record<TermKind, "outline" | "info" | "success" | "neutral"> = {
  concept: "outline",
  metric: "info",
  calculation: "success",
  query: "neutral",
};

const KIND_LABEL: Record<TermKind, string> = {
  concept: "Concept",
  metric: "Metric",
  calculation: "Calculation",
  query: "Query",
};

export interface TermCardProps {
  term: GlossaryTerm;
  /** slug → term, for resolving related-term chip labels. */
  resolve: (slug: string) => GlossaryTerm | undefined;
  onNavigate: (slug: string) => void;
  onTag?: (tag: string) => void;
  onEdit?: (term: GlossaryTerm) => void;
  /** Admin approve; shown only for in-review terms. */
  onApprove?: (term: GlossaryTerm) => void;
  /** True while this card is the freshly-navigated target (brief highlight). */
  highlighted?: boolean;
}

function approvedLine(term: GlossaryTerm): string | null {
  if (term.status !== "approved") return null;
  const when = term.approved_at
    ? new Date(term.approved_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;
  const who = term.approved_by_email;
  if (who && when) return `Approved by ${who} · ${when}`;
  if (when) return `Approved · ${when}`;
  return "Approved";
}

export function TermCard({
  term,
  resolve,
  onNavigate,
  onTag,
  onEdit,
  onApprove,
  highlighted,
}: TermCardProps) {
  const inReview = term.status !== "approved";
  return (
    <Card
      id={`term-${term.slug}`}
      className={cn(
        "scroll-mt-24 transition-shadow",
        highlighted && "ring-2 ring-brand ring-offset-2",
      )}
    >
      <CardContent className="space-y-4 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold tracking-tight">{term.term}</h3>
              <Badge variant={inReview ? "warning" : "success"}>
                {inReview ? "In Review" : "Approved"}
              </Badge>
              {term.kinds.map((k) => (
                <Badge key={k} variant={KIND_VARIANT[k]}>
                  {KIND_LABEL[k]}
                </Badge>
              ))}
            </div>
            {term.aliases && term.aliases.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Also: {term.aliases.join(" · ")}
              </p>
            ) : null}
            {term.tags.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                {term.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => onTag?.(tag)}
                    disabled={!onTag}
                    className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-default disabled:hover:bg-muted disabled:hover:text-muted-foreground"
                    title={onTag ? `Filter by "${tag}"` : tag}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {onApprove && inReview ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onApprove(term)}
                aria-label={`Approve ${term.term}`}
              >
                <Check className="mr-1 h-4 w-4" />
                Approve
              </Button>
            ) : null}
            {onEdit ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(term)}
                aria-label={`Edit ${term.term}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>

        {/* Definition */}
        <MarkdownView source={term.definition} />

        {/* Equation */}
        {term.equation ? (
          <CodeBlock code={term.equation} label="Equation" tone="brand" />
        ) : null}

        {/* Query — how to reproduce the number (Snowflake / Cinch Ops / other) */}
        {term.query ? <CodeBlock code={term.query} label="Query" /> : null}

        {/* Related — clickable cross-links */}
        {term.related && term.related.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Related
            </span>
            {term.related.map((slug) => {
              const target = resolve(slug);
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => onNavigate(slug)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                    target
                      ? "border-brand/30 bg-brand-light text-brand hover:bg-brand hover:text-white"
                      : "border-dashed border-border text-muted-foreground",
                  )}
                  title={target ? `Jump to ${target.term}` : `${slug} (not found)`}
                >
                  {target ? target.term : slug}
                </button>
              );
            })}
          </div>
        ) : null}

        {/* Approval audit line */}
        {approvedLine(term) ? (
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Check className="h-3 w-3 text-success" />
            {approvedLine(term)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
